import React, { useState } from "react";
import { Categoria } from "@/api/entities";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import CategoriaCard from "../components/categorias/CategoriaCard";
import CategoriaDialog from "../components/categorias/CategoriaDialog";

export default function Categorias() {
  const [showDialog, setShowDialog] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState(null);
  const queryClient = useQueryClient();

  const { data: categorias = [], isLoading } = useQuery({
    queryKey: ['categorias'],
    queryFn: () => Categoria.list({}, { orderBy: 'orden', orderDirection: 'asc' }),
  });

  const createMutation = useMutation({
    mutationFn: (data) => Categoria.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categorias'] });
      setShowDialog(false);
      setEditingCategoria(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => Categoria.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categorias'] });
      setShowDialog(false);
      setEditingCategoria(null);
    },
  });

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(categorias);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Actualizar el orden
    for (let i = 0; i < items.length; i++) {
      await updateMutation.mutateAsync({
        id: items[i].id,
        data: { orden: i }
      });
    }
  };

  const handleSubmit = (data) => {
    if (editingCategoria) {
      updateMutation.mutate({ id: editingCategoria.id, data });
    } else {
      const orden = categorias.length;
      createMutation.mutate({ ...data, orden });
    }
  };

  const handleEdit = (categoria) => {
    setEditingCategoria(categoria);
    setShowDialog(true);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Categorías</h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">Gestiona las categorías de productos</p>
          </div>
          <Button 
            onClick={() => {
              setEditingCategoria(null);
              setShowDialog(true);
            }}
            className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Categoría
          </Button>
        </div>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="categorias">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              {categorias.map((categoria, index) => (
                <Draggable key={categoria.id} draggableId={categoria.id} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <CategoriaCard
                        categoria={categoria}
                        onEdit={handleEdit}
                        onToggleActive={(id, activa) => 
                          updateMutation.mutate({ id, data: { activa } })
                        }
                        isDragging={snapshot.isDragging}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <CategoriaDialog
        open={showDialog}
        onClose={() => {
          setShowDialog(false);
          setEditingCategoria(null);
        }}
        onSubmit={handleSubmit}
        categoria={editingCategoria}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  );
}